import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const CART_TYPES = ['experience', 'accommodation', 'transfer', 'package', 'rent-a-car'];
const STORAGE_KEY = 'eco-cart-v1';
const LEGACY_KEY = 'eco-my-trip';
const REQUEST_ID_KEY = 'eco-cart-request-id';

const LEGACY_TYPE_OF = (entry) => {
  if (entry.type === 'tour' || entry.kind === 'package') return 'package';
  if (entry.kind === 'transfer') return 'transfer';
  if (entry.kind === 'accommodation') return 'accommodation';
  return 'experience';
};

const readCart = () => {
  try {
    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (current.length || localStorage.getItem('eco-cart-migrated')) return Array.isArray(current) ? current : [];
    // One-time migration of the legacy "Adicionar à minha viagem" list.
    const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
    const migrated = (Array.isArray(legacy) ? legacy : []).filter((e) => e?.slug && e?.name).map((e) => ({
      key: `${LEGACY_TYPE_OF(e)}:${e.slug}`,
      type: LEGACY_TYPE_OF(e),
      slug: e.slug,
      name: e.name,
      destination: e.destination || e.island || '',
      qty: 1,
    }));
    localStorage.setItem('eco-cart-migrated', '1');
    if (migrated.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    return migrated;
  } catch {
    return [];
  }
};

const newRequestId = () => (crypto.randomUUID ? crypto.randomUUID() : `cart-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readCart);

  const persist = useCallback((next) => {
    setItems(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const add = useCallback((item) => {
    if (!item?.slug || !item?.name || !CART_TYPES.includes(item.type)) return { added: false, reason: 'invalid' };
    const key = `${item.type}:${item.slug}`;
    let added = false;
    setItems((current) => {
      if (current.some((x) => x.key === key)) return current;
      added = true;
      const next = [...current, {
        key, type: item.type, slug: item.slug, name: item.name,
        destination: item.destination || item.island || '',
        qty: 1,
        ...(item.priceEur ? { price_eur_snapshot: Number(item.priceEur) } : {}),
      }];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    return { added, reason: added ? null : 'duplicate' };
  }, []);

  const remove = useCallback((key) => persist(items.filter((x) => x.key !== key)), [items, persist]);

  const update = useCallback((key, patch) => {
    persist(items.map((x) => (x.key === key ? { ...x, ...patch, key: x.key, type: x.type, slug: x.slug } : x)));
  }, [items, persist]);

  const clear = useCallback(() => persist([]), [persist]);

  /** Stable idempotency key for the combined quotation request — regenerated after a successful submit. */
  const getRequestId = useCallback(() => {
    let id = localStorage.getItem(REQUEST_ID_KEY);
    if (!id) {
      id = newRequestId();
      localStorage.setItem(REQUEST_ID_KEY, id);
    }
    return id;
  }, []);

  const rotateRequestId = useCallback(() => {
    const id = newRequestId();
    localStorage.setItem(REQUEST_ID_KEY, id);
    return id;
  }, []);

  const hasPackage = items.some((x) => x.type === 'package');
  const hasSeparates = items.some((x) => x.type === 'accommodation' || x.type === 'transfer');

  const value = useMemo(() => ({
    items,
    count: items.length,
    add,
    remove,
    update,
    clear,
    includes: (type, slug) => items.some((x) => x.key === `${type}:${slug}`),
    getRequestId,
    rotateRequestId,
    mayOverlapPackage: hasPackage && hasSeparates,
  }), [items, add, remove, update, clear, getRequestId, rotateRequestId, hasPackage, hasSeparates]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext) || {
  items: [], count: 0, add: () => ({ added: false }), remove: () => {}, update: () => {}, clear: () => {},
  includes: () => false, getRequestId: () => '', rotateRequestId: () => '', mayOverlapPackage: false,
};
